<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const store = useStore();

const firecrawlConfig = computed(
  () => store.getters['studioDocuments/getFirecrawlConfig']
);

const apiKeyInput = ref('');
const showApiKeyInput = ref(false);
const showApiKeyPassword = ref(false);
const isSavingApiKey = ref(false);

const toggleApiKeyEdit = () => {
  showApiKeyInput.value = !showApiKeyInput.value;
  if (!showApiKeyInput.value) {
    apiKeyInput.value = '';
    showApiKeyPassword.value = false;
  }
};

const saveApiKey = async () => {
  if (!apiKeyInput.value.trim()) {
    useAlert(t('AI.KNOWLEDGE.API.EMPTY_API_KEY'));
    return;
  }
  isSavingApiKey.value = true;
  try {
    const result = await store.dispatch(
      'studioDocuments/saveFirecrawlConfig',
      apiKeyInput.value.trim()
    );
    if (result.success) {
      useAlert(result.message);
      showApiKeyInput.value = false;
      apiKeyInput.value = '';
      showApiKeyPassword.value = false;
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('AI.KNOWLEDGE.API.ERROR_SAVE_API_KEY')
    );
  } finally {
    isSavingApiKey.value = false;
  }
};

const removeApiKey = async () => {
  isSavingApiKey.value = true;
  try {
    const result = await store.dispatch(
      'studioDocuments/saveFirecrawlConfig',
      ''
    );
    if (result.success) {
      useAlert(result.message);
      showApiKeyInput.value = false;
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('AI.KNOWLEDGE.API.ERROR_REMOVE_API_KEY')
    );
  } finally {
    isSavingApiKey.value = false;
  }
};

onMounted(() => {
  store.dispatch('studioDocuments/fetchFirecrawlConfig');
});
</script>

<template>
  <SectionLayout
    :title="t('AI.KNOWLEDGE.FIRECRAWL.TITLE')"
    :description="t('AI.KNOWLEDGE.FIRECRAWL.NOT_CONFIGURED_INFO')"
  >
    <template #headerActions>
      <span
        v-if="firecrawlConfig.configured"
        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-lucide-check-circle size-3.5" />
        {{
          firecrawlConfig.source === 'global'
            ? t('AI.KNOWLEDGE.FIRECRAWL.STATUS_GLOBAL')
            : t('AI.KNOWLEDGE.FIRECRAWL.STATUS_CONFIGURED')
        }}
      </span>
      <span
        v-else
        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-n-amber-3 text-n-amber-11"
      >
        <span class="i-lucide-alert-circle size-3.5" />
        {{ t('AI.KNOWLEDGE.FIRECRAWL.STATUS_OPTIONAL') }}
      </span>
    </template>

    <!-- Account key configured + not editing -->
    <div
      v-if="
        !showApiKeyInput &&
        firecrawlConfig.configured &&
        firecrawlConfig.source === 'account'
      "
      class="flex items-center justify-between"
    >
      <span
        class="font-mono text-sm bg-n-alpha-2 px-2 py-1 rounded text-n-slate-11"
      >
        {{ firecrawlConfig.masked_key }}
      </span>
      <div class="flex gap-2">
        <Button
          icon="i-lucide-pencil"
          color="slate"
          variant="ghost"
          size="xs"
          @click="toggleApiKeyEdit"
        >
          {{ t('AI.KNOWLEDGE.BUTTONS.CHANGE') }}
        </Button>
        <Button
          icon="i-lucide-trash-2"
          color="ruby"
          variant="ghost"
          size="xs"
          :is-loading="isSavingApiKey"
          @click="removeApiKey"
        >
          {{ t('AI.KNOWLEDGE.BUTTONS.REMOVE') }}
        </Button>
      </div>
    </div>

    <!-- Global key info — sem chave de account, mas a env tem uma global -->
    <div
      v-else-if="
        !showApiKeyInput &&
        firecrawlConfig.configured &&
        firecrawlConfig.source === 'global'
      "
      class="flex items-center justify-between"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('AI.KNOWLEDGE.FIRECRAWL.GLOBAL_KEY_INFO') }}
      </p>
      <Button
        icon="i-lucide-plus"
        color="slate"
        variant="ghost"
        size="xs"
        @click="toggleApiKeyEdit"
      >
        {{ t('AI.KNOWLEDGE.BUTTONS.USE_OWN_KEY') }}
      </Button>
    </div>

    <!-- Not configured or edit mode — input -->
    <div v-else>
      <p
        v-if="!firecrawlConfig.configured && !showApiKeyInput"
        class="text-sm text-n-slate-11 mb-3"
      >
        {{ t('AI.KNOWLEDGE.FIRECRAWL.NOT_CONFIGURED_INFO') }}
        <a
          href="https://www.firecrawl.dev/app/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-brand-ink hover:underline"
        >
          {{ t('AI.KNOWLEDGE.FIRECRAWL.GET_API_KEY') }}
        </a>
      </p>
      <div class="flex gap-2">
        <div class="flex-1 relative">
          <Input
            v-model="apiKeyInput"
            :type="showApiKeyPassword ? 'text' : 'password'"
            :placeholder="t('AI.KNOWLEDGE.FIRECRAWL.KEY_PLACEHOLDER')"
            class="pr-10"
          />
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-n-alpha-2 transition-colors"
            @click="showApiKeyPassword = !showApiKeyPassword"
          >
            <span
              class="size-4 text-n-slate-11"
              :class="[
                showApiKeyPassword ? 'i-lucide-eye-off' : 'i-lucide-eye',
              ]"
            />
          </button>
        </div>
        <Button
          icon="i-lucide-save"
          color="blue"
          size="sm"
          :is-loading="isSavingApiKey"
          :disabled="!apiKeyInput.trim()"
          @click="saveApiKey"
        >
          {{ t('AI.KNOWLEDGE.BUTTONS.SAVE') }}
        </Button>
        <Button
          v-if="showApiKeyInput"
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          size="sm"
          @click="toggleApiKeyEdit"
        >
          {{ t('AI.KNOWLEDGE.BUTTONS.CANCEL') }}
        </Button>
      </div>
    </div>
  </SectionLayout>
</template>
